import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useSettings } from '../../contexts/SettingsContext';
import { motion, AnimatePresence } from 'framer-motion';
import {
  useCalendarData,
  useClientsData,
  usePackagesData,
  useSuppliersData,
  useFinancialData,
  createCalendarEvent,
  updateCalendarEvent,
  deleteCalendarEvent
} from '../../services/dataService';

import jsPDF from 'jspdf';
import 'jspdf-autotable';

import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  IconButton,
  Button,
  TextField,
  Chip,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Snackbar,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  ListItemAvatar,
  Divider,
  Paper,
  ToggleButton,
  ToggleButtonGroup,
  Badge,
  Tooltip,
  Fab,
  Tabs,
  Tab,
  Autocomplete,
  LinearProgress,
  Stack,
  AvatarGroup,
  Skeleton,
  Grow,
  useTheme,
  useMediaQuery,
  CircularProgress
} from '@mui/material';
import {
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Today as TodayIcon,
  Add as AddIcon,
  Event as EventIcon,
  FlightTakeoff as FlightIcon,
  FlightLand as LandingIcon,
  Business as BusinessIcon,
  Person as PersonIcon,
  LocationOn as LocationIcon,
  AccessTime as TimeIcon,
  CalendarMonth as MonthIcon,
  CalendarViewWeek as WeekIcon,
  CalendarViewDay as DayIcon,
  Notifications as NotificationIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  MoreVert as MoreVertIcon,
  Check as CheckIcon,
  Schedule as ScheduleIcon,
  Warning as WarningIcon,
  Star as StarIcon,
  Analytics as AnalyticsIcon,
  Link as LinkIcon,
  TrendingUp as TrendingUpIcon,
  Info as InfoIcon,
  MonetizationOn as MoneyIcon,
  Group as GroupIcon,
  Flight as FlightMainIcon,
  Hotel as HotelIcon,
  Restaurant as RestaurantIcon,
  LocalActivity as ActivityIcon,
  Assessment as AssessmentIcon,
  Timeline as TimelineIcon,
  PieChart as PieChartIcon,
  BarChart as BarChartIcon,
  CalendarToday as CalendarTodayIcon,
  EventAvailable as EventAvailableIcon,
  EventBusy as EventBusyIcon,
  School as SchoolIcon,
  ViewList as ViewListIcon,
  ViewList as TableViewIcon,
  ViewModule as CardViewIcon,
  RocketLaunch,
  AutoAwesome,
  ArrowForward,
  Diamond,
  GetApp as ExportIcon,
  PictureAsPdf as PdfIcon
} from '@mui/icons-material';

// Custom CSS for dialog styling
const dialogStyles = `
  .modern-dialog .MuiPaper-root {
    background: rgba(255, 255, 255, 0.1) !important;
    backdrop-filter: blur(20px) !important;
    border: 1px solid rgba(255, 255, 255, 0.2) !important;
    border-radius: 24px !important;
  }
  .modern-dialog .MuiDialogContent-root,
  .modern-dialog .MuiDialogTitle-root,
  .modern-dialog .MuiDialogActions-root {
    background: transparent !important;
    background-color: transparent !important;
  }
`;

// Inject styles
if (typeof document !== 'undefined') {
  const styleSheet = document.createElement('style');
  styleSheet.type = 'text/css';
  styleSheet.innerText = dialogStyles;
  document.head.appendChild(styleSheet);
}

// FloatingActionCard component from Dashboard
const FloatingActionCard = ({ title, icon, color, action, description, badge, onClick }) => {
  // Get theme and check if mobile
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  return (
    <Card sx={{
    background: `linear-gradient(135deg, ${color}15, white)`,
      border: `1px solid ${color}30`,
      borderRadius: { xs: 3, md: 4 },
      overflow: 'hidden',
      position: 'relative',
      cursor: 'pointer',
      height: '100%', // Ensure consistent height
    }} onClick={onClick}>
      <CardContent sx={{ 
        p: { xs: 2, sm: 2.5, md: 3 }, 
        position: 'relative', 
        zIndex: 1,
        height: '100%',
        display: 'flex',
        flexDirection: 'column'
      }}>
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: { xs: 1, md: 2 }, 
          mb: { xs: 1.5, md: 2 },
          flexWrap: 'wrap'
        }}>
          <Avatar className="action-icon" sx={{ 
            background: `linear-gradient(135deg, ${color}, ${color}cc)`,
            width: { xs: 48, sm: 52, md: 56 },
            height: { xs: 48, sm: 52, md: 56 },
            boxShadow: `0 8px 25px ${color}40`,
            '& .MuiSvgIcon-root': {
              fontSize: { xs: 26, sm: 28, md: 30 }
            }
          }}>
            {icon}
          </Avatar>
          {badge && (
            <Chip 
              label={badge} 
              size="small" 
              sx={{ 
                background: `linear-gradient(135deg, ${color}, ${color}dd)`,
                color: 'white',
                fontWeight: 600,
                height: { xs: '20px', md: '24px' },
                fontSize: { xs: '0.7rem', md: '0.75rem' }
              }}
            />
          )}
        </Box>
        <Typography 
          variant="h6" 
          sx={{ 
            fontWeight: 700, 
            mb: { xs: 0.5, md: 1 }, 
            color: 'rgba(51,65,85,0.9)',
            lineHeight: 1.3,
            fontSize: { xs: '1.1rem', sm: '1.2rem', md: '1.3rem' }
          }}
        >
          {title}
        </Typography>
        <Typography 
          variant="body2" 
          sx={{ 
            color: 'rgba(51,65,85,0.7)',
            fontSize: { xs: '0.75rem', md: '0.875rem' },
            display: { xs: '-webkit-box', md: 'block' },
            WebkitLineClamp: { xs: 2, md: 'none' },
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            textOverflow: 'ellipsis'
          }}
        >
          {description}
        </Typography>
      </CardContent>
    </Card>
  );
};

// EnhancedStatCard from Dashboard
const EnhancedStatCard = ({ title, value, icon, gradient, delay, onClick, subtitle, chart, trend, progress }) => (
  <Grow in={true} style={{ transformOrigin: '0 0 0' }} timeout={1000}>
    <Card
      onClick={onClick}
      sx={{
        height: '100%',
        background: gradient ? `linear-gradient(135deg, ${gradient[0]}, ${gradient[1]})` : 'white',
        color: gradient ? 'white' : 'inherit',
        borderRadius: 3,
        boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
        transition: 'transform 0.3s, box-shadow 0.3s',
        cursor: onClick ? 'pointer' : 'default',
        '&:hover': onClick ? {
          transform: 'translateY(-5px)',
          boxShadow: '0 10px 30px rgba(0,0,0,0.15)',
        } : {},
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <CardContent sx={{ flexGrow: 1, p: { xs: 2, md: 3 } }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
          <Box>
            <Typography variant="body2" sx={{ 
              opacity: gradient ? 0.9 : 0.7, 
              mb: 0.5, 
              fontWeight: 500,
              fontSize: { xs: '0.8rem', md: '0.875rem' }
            }}>
              {title}
            </Typography>
            <Typography variant="h5" sx={{ 
              fontWeight: 700,
              fontSize: { xs: '1.5rem', md: '1.75rem' }
            }}>
              {value}
            </Typography>
            {subtitle && (
              <Typography variant="body2" sx={{ 
                opacity: gradient ? 0.9 : 0.7, 
                mt: 0.5,
                fontSize: { xs: '0.75rem', md: '0.875rem' }
              }}>
                {subtitle}
              </Typography>
            )}
            {trend && (
              <Chip 
                label={trend} 
                size="small" 
                sx={{ 
                  mt: 1,
                  backgroundColor: trend.startsWith('+') ? 
                    (gradient ? 'rgba(255,255,255,0.3)' : '#059669') : 
                    (gradient ? 'rgba(255,255,255,0.3)' : '#dc2626'),
                  color: gradient ? 'white' : 'white',
                  fontWeight: 600,
                  fontSize: { xs: '0.65rem', md: '0.75rem' },
                  height: { xs: 20, md: 24 }
                }}
              />
            )}
          </Box>
          <Avatar
            sx={{
              bgcolor: gradient ? 'rgba(255,255,255,0.2)' : 'primary.light',
              color: gradient ? 'white' : 'primary.main',
              width: { xs: 40, md: 48 },
              height: { xs: 40, md: 48 }
            }}
          >
            {icon}
          </Avatar>
        </Box>
        
        {progress && (
          <Box sx={{ mt: 1 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
              <Typography variant="caption" sx={{ opacity: gradient ? 0.9 : 0.7 }}>
                Progression
              </Typography>
              <Typography variant="caption" sx={{ opacity: gradient ? 0.9 : 0.7 }}>
                {progress}%
              </Typography>
            </Box>
            <LinearProgress 
              variant="determinate" 
              value={progress} 
              sx={{ 
                height: 6, 
                borderRadius: 3,
                backgroundColor: gradient ? 'rgba(255,255,255,0.2)' : 'rgba(99,102,241,0.2)',
                '& .MuiLinearProgress-bar': {
                  backgroundColor: gradient ? 'white' : 'primary.main',
                }
              }} 
            />
          </Box>
        )}
        
        {chart && (
          <Box sx={{ mt: 2, height: { xs: 40, md: 60 } }}>
            {chart}
          </Box>
        )}
      </CardContent>
    </Card>
  </Grow>
);

// Simple Chart Components
const SimpleEventChart = ({ data, title }) => {
  const total = data.reduce((sum, item) => sum + item.value, 0);
  
  return (
    <Card sx={{ 
      mb: 2, 
      borderRadius: 3,
      background: 'rgba(255, 255, 255, 0.1)',
      backdropFilter: 'blur(20px)',
      border: '1px solid rgba(255, 255, 255, 0.2)',
      boxShadow: '0 4px 20px rgba(0,0,0,0.08)'
    }}>
      <CardContent>
        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'white' }}>
          <PieChartIcon sx={{ color: 'rgba(255, 255, 255, 0.7)' }} />
          {title}
        </Typography>
        <Box sx={{ mt: 2 }}>
          {data.map((item, index) => (
            <Box key={index} sx={{ mb: 1 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>{item.label}</Typography>
                <Typography variant="body2" fontWeight={600} sx={{ color: 'white' }}>{item.value}</Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={(item.value / total) * 100}
                sx={{ 
                  height: 8, 
                  borderRadius: 1,
                  backgroundColor: 'rgba(255, 255, 255, 0.2)',
                  '& .MuiLinearProgress-bar': {
                    backgroundColor: item.color === 'secondary' ? '#8b5cf6' : '#6366f1'
                  }
                }}
              />
            </Box>
          ))}
        </Box>
      </CardContent>
    </Card>
  );
};

const SimpleActivityChart = ({ activities }) => {
  const days = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'];
  const maxValue = Math.max(...activities);
  
  return (
    <Card sx={{ 
      mb: 2, 
      borderRadius: 3,
      background: 'rgba(255, 255, 255, 0.1)',
      backdropFilter: 'blur(20px)',
      border: '1px solid rgba(255, 255, 255, 0.2)',
      boxShadow: '0 4px 20px rgba(0,0,0,0.08)'
    }}>
      <CardContent>
        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'white' }}>
          <BarChartIcon sx={{ color: 'rgba(255, 255, 255, 0.7)' }} />
          Activité Hebdomadaire
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'end', gap: 1, height: 100, mt: 2 }}>
          {activities.map((value, index) => (
            <Box key={index} sx={{ flex: 1, textAlign: 'center' }}>
              <Box
                sx={{
                  height: `${(value / maxValue) * 80}px`,
                  background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
                  borderRadius: 1,
                  mb: 1,
                  minHeight: 4
                }}
              />
              <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                {days[index]}
              </Typography>
            </Box>
          ))}
        </Box>
      </CardContent>
    </Card>
  );
};

const Calendrier = () => {
  const { settings, formatCurrency, formatDate, calculateTotalWithTax } = useSettings();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [viewMode, setViewMode] = useState('month');
  const [displayMode, setDisplayMode] = useState('table'); // For table vs card view in list mode
  const [selectedDate, setSelectedDate] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [selectedTab, setSelectedTab] = useState(0);
  const [selectedClient, setSelectedClient] = useState(null);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [filterType, setFilterType] = useState('all');

  // Use real-time interconnected data hooks
  const { events, loading: eventsLoading, error: eventsError, stats: eventsStats } = useCalendarData({
    eventType: filterType !== 'all' ? filterType : undefined,
    startDate: new Date(currentDate.getFullYear(), currentDate.getMonth(), 1).toISOString().split('T')[0],
    endDate: new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0).toISOString().split('T')[0]
  });

  // Local copy of events to allow optimistic UI updates
  const [allEvents, setAllEvents] = useState([]);

  useEffect(() => {
    setAllEvents(events);
  }, [events]);
  const { clients, loading: clientsLoading } = useClientsData();
  const { packages, loading: packagesLoading } = usePackagesData();
  const { suppliers, loading: suppliersLoading } = useSuppliersData();
  const { financialData, loading: financialLoading, error: financialError } = useFinancialData();
  
  // Chargement optimisé - affichage immédiat du calendrier
  const isLoading = false;

  const [formData, setFormData] = useState({
    titre: '',
    type: 'Professionnel',
    date: '',
    heure: '',
    client: null,
    package: null,
    destination: '',
    statut: 'Planifié',
    description: '',
    participants: 1,
    revenue: 0
  });

  // Get events for a specific date
  const getEventsForDate = (date) => {
    const dateStr = date.toISOString().split('T')[0];
    console.log(`Looking for events on date: ${dateStr}`);
    
    const filteredEvents = (allEvents || []).filter(event => {
      try {
        const eventDateStr = new Date(event.date).toISOString().split('T')[0];
        console.log(`Event ${event.id || 'new'}: ${event.titre}, date=${event.date}, normalized=${eventDateStr}`);
        return eventDateStr === dateStr;
      } catch (error) {
        console.error(`Error processing event date: ${event.date}`, error);
        return false;
      }
    });
    
    console.log(`Found ${filteredEvents.length} events for ${dateStr}`);
    return filteredEvents;
  };

  // Get events for current month
  const getEventsForMonth = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    console.log(`Looking for events in month: ${month+1}/${year}, total events: ${events?.length || 0}`);
    
    const filteredEvents = (allEvents || []).filter(event => {
      try {
        const eventDate = new Date(event.date);
        const matches = eventDate.getFullYear() === year && eventDate.getMonth() === month;
        if (matches) {
          console.log(`Month match: ${event.titre} on ${event.date}`);
        }
        return matches;
      } catch (error) {
        console.error(`Error processing event date for month filter: ${event.date}`, error);
        return false;
      }
    });
    
    console.log(`Found ${filteredEvents.length} events for month ${month+1}/${year}`);
    return filteredEvents;
  };

  // Navigate calendar
  const navigateMonth = (direction) => {
    setCurrentDate(prev => {
      const newDate = new Date(prev);
      newDate.setMonth(prev.getMonth() + direction);
      return newDate;
    });
  };

  const goToToday = () => {
    setCurrentDate(new Date());
  };

  // Generate calendar days
  const generateCalendarDays = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const startDate = new Date(firstDay);
    startDate.setDate(startDate.getDate() - firstDay.getDay());

    // This will return an array of date objects for the calendar grid
    const days = [];
    for (let i = 0; i < 42; i++) {
      const date = new Date(startDate);
      days.push(date);
      startDate.setDate(startDate.getDate() + 1);
    }
    return days;
  };

  // Flag to hide Actions Rapides
  const showQuickActions = false;

  // Function to format currency correctly
  const formatCurrencyForPDF = (amount) => {
    // Convert number to string with spaces as thousand separators
    const numberPart = Math.round(amount || 0).toString().replace(/\B(?=(\d{3})+(?!\d))/g, " ");
    return `${numberPart} DZD`;
  };

  // PDF Export Function
  const exportCalendrierToPDF = () => {
    try {
      // Create a new PDF document
      const doc = new jsPDF('landscape');
      
      // Document dimensions
      const pageWidth = doc.internal.pageSize.width;
      const pageHeight = doc.internal.pageSize.height;
      const margin = 20;
      const textColor = [255, 255, 255]; // White text for dark theme
      
      // Set background to match dark theme
      doc.setFillColor(30, 27, 75); // Dark purple background
      doc.rect(0, 0, pageWidth, pageHeight, 'F');
      
      // Add header
      doc.setTextColor(textColor[0], textColor[1], textColor[2]);
      doc.setFontSize(22);
      doc.setFont('helvetica', 'bold');
      doc.text('Rapport Calendrier', pageWidth/2, 30, { align: 'center' });
      
      // Add subtitle
      doc.setFontSize(14);
      doc.setFont('helvetica', 'normal');
      const monthYear = `${currentDate.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' })}`;
      doc.text(`Période: ${monthYear}`, pageWidth/2, 45, { align: 'center' });
      
      // Add stats summary
      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      doc.text("Résumé des Événements", margin, 65);
      
      const eventsThisMonth = getEventsForMonth();
      const totalEvents = eventsThisMonth.length;
      const confirmedEvents = eventsThisMonth.filter(e => e.statut === 'Confirmé').length;
      const totalRevenue = eventsThisMonth.reduce((sum, event) => sum + (event.revenue || 0), 0);
      
      const statsData = [
        ['Total Événements', totalEvents.toString()],
        ['Événements Confirmés', `${confirmedEvents} (${Math.round((confirmedEvents/totalEvents)*100) || 0}%)`],
        ['Revenus Estimés', formatCurrencyForPDF(totalRevenue)],
        ['Taux d\'Occupation', `${Math.round((totalEvents/30)*100) || 0}%`]
      ];
      
      doc.autoTable({
        startY: 75,
        body: statsData,
        theme: 'grid',
        headStyles: {
          fillColor: [30, 64, 175],
          textColor: [255, 255, 255],
          fontSize: 11
        },
        bodyStyles: {
          fillColor: [45, 42, 95],
          textColor: [255, 255, 255],
          fontSize: 10
        },
        styles: {
          fontSize: 10,
          cellPadding: 5
        }
      });
      
      // Add events table
      if (eventsThisMonth.length > 0) {
        doc.setFontSize(12);
        doc.setFont('helvetica', 'bold');
        doc.text("Détails des Événements", margin, doc.autoTable.previous.finalY + 20);
        
        const eventsTableData = eventsThisMonth.map(event => [
          event.titre || 'Sans titre',
          event.type || 'N/A',
          event.date ? new Date(event.date).toLocaleDateString('fr-FR') : 'N/A',
          event.clientNom || 'N/A',
          event.destination || 'N/A',
          formatCurrencyForPDF(event.revenue || 0),
          event.statut || 'N/A'
        ]);
        
        doc.autoTable({
          startY: doc.autoTable.previous.finalY + 25,
          head: [['Titre', 'Type', 'Date', 'Client', 'Destination', 'Revenus', 'Statut']],
          body: eventsTableData,
          theme: 'grid',
          headStyles: {
            fillColor: [30, 64, 175],
            textColor: [255, 255, 255],
            fontSize: 10
          },
          bodyStyles: {
            fillColor: [45, 42, 95],
            textColor: [255, 255, 255],
            fontSize: 9
          },
          styles: {
            fontSize: 9,
            cellPadding: 3
          },
          columnStyles: {
            0: { cellWidth: 35 },
            1: { cellWidth: 25 },
            2: { cellWidth: 25 },
            3: { cellWidth: 30 },
            4: { cellWidth: 30 },
            5: { cellWidth: 25 },
            6: { cellWidth: 20 }
          }
        });
      }
      
      // Add footer
      doc.setFontSize(10);
      doc.setTextColor(200, 200, 200);
      doc.text(`Généré le ${new Date().toLocaleDateString('fr-FR')} - TravelIT`, margin, pageHeight - 10);
      doc.text(`Page 1/1`, pageWidth - margin, pageHeight - 10, { align: 'right' });
      
      // Save the PDF
      doc.save(`calendrier-${monthYear.replace(' ', '-').toLowerCase()}.pdf`);
      
      // Success notification
      setSnackbar({
        open: true,
        message: 'Calendrier exporté en PDF avec succès',
        severity: 'success'
      });
      
    } catch (error) {
      console.error('Erreur lors de la génération du PDF:', error);
      setSnackbar({
        open: true,
        message: 'Erreur lors de l\'export PDF',
        severity: 'error'
      });
    }
  };
  
  // Open event dialog
  const handleOpenDialog = (event = null, date = null) => {
    if (event) {
      // Edit existing event
      setEditingEvent(event);
      
      // Find related client and package for pre-selection
      const relatedClient = event.client ? clients.find(c => c.id === event.client.id) : null;
      const relatedPackage = event.package ? packages.find(p => p.id === event.package.id) : null;
      
      setSelectedClient(relatedClient);
      setSelectedPackage(relatedPackage);
      
      setFormData({
        titre: event.titre,
        type: event.type,
        date: event.date,
        heure: event.heure,
        client: relatedClient,
        package: relatedPackage,
        destination: event.destination,
        statut: event.statut,
        description: event.description,
        participants: event.participants,
        revenue: event.revenue
      });
    } else {
      // Create new event
      setEditingEvent(null);
      
      // Use the clicked date if available
      const selectedDate = date ? date.toISOString().split('T')[0] : new Date().toISOString().split('T')[0];
      
      setSelectedClient(null);
      setSelectedPackage(null);
      
      setFormData({
        titre: '',
        type: 'Professionnel',
        date: selectedDate,
        heure: '09:00',
        client: null,
        package: null,
        destination: '',
        statut: 'Planifié',
        description: '',
        participants: 1,
        revenue: 0
      });
    }
    setOpenDialog(true);
  };

  // Close dialog
  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingEvent(null);
    setSelectedClient(null);
    setSelectedPackage(null);
  };

  // Save event
  const handleSaveEvent = async () => {
    try {
      // Validate required fields
      if (!formData.titre || !formData.date) {
        setSnackbar({
          open: true,
          message: 'Veuillez remplir tous les champs obligatoires',
          severity: 'error'
        });
        return;
      }

      // Create event data object
      const eventData = {
        titre: formData.titre,
        type: formData.type,
        date: formData.date,
        heure: formData.heure,
        client: formData.client ? {
          id: formData.client.id,
          nom: formData.client.nom,
          prenom: formData.client.prenom
        } : null,
        package: formData.package ? {
          id: formData.package.id,
          titre: formData.package.titre
        } : null,
        destination: formData.destination,
        statut: formData.statut,
        description: formData.description,
        participants: formData.participants,
        revenue: formData.revenue,
        couleur: getCouleurByType(formData.type)
      };

      console.log('Saving event data:', eventData);
      
      let result;
      if (editingEvent) {
        // Update existing event
        result = await updateCalendarEvent(editingEvent.id, eventData);
      } else {
        // Create new event
        result = await createCalendarEvent(eventData);
      }
      
      console.log('API response:', result);
      
      // Show success message
      setSnackbar({
        open: true,
        message: editingEvent ? 'Événement modifié avec succès' : 'Nouvel événement ajouté',
        severity: 'success'
      });

      const savedEvent = result && result.data ? result.data : result; // backend may wrap in {data: ...}

      if (savedEvent && savedEvent.titre) {
        console.log('Manually adding event to local state:', savedEvent);
        setAllEvents(prevEvents => {
          if (editingEvent) {
            // Replace existing event
            return prevEvents.map(ev => ev.id === editingEvent.id ? savedEvent : ev);
          }
          // Add new event
          return [...prevEvents, savedEvent];
        });
      }

      // Force refresh events
      window.dispatchEvent(new CustomEvent('calendarDataUpdate'));

      // Close the dialog
      handleCloseDialog();
    } catch (error) {
      console.error('Error saving event:', error);
      setSnackbar({
        open: true,
        message: error.message || 'Une erreur est survenue lors de l\'enregistrement de l\'événement',
        severity: 'error'
      });
    }
  };

  // Delete event
  const handleDeleteEvent = async () => {
    try {
      if (!editingEvent) return;

      // Delete event via API
      await deleteCalendarEvent(editingEvent.id);
      
      // Show success message
      setSnackbar({
        open: true,
        message: 'Événement supprimé avec succès',
        severity: 'success'
      });

      // Remove from local state immediately
      setAllEvents(prev => prev.filter(ev => ev.id !== editingEvent.id));

      // Force refresh events
      window.dispatchEvent(new CustomEvent('calendarDataUpdate'));

      // Close the dialog
      handleCloseDialog();
    } catch (error) {
      console.error('Error deleting event:', error);
      setSnackbar({
        open: true,
        message: error.message || 'Une erreur est survenue lors de la suppression de l\'événement',
        severity: 'error'
      });
    }
  };

  // Get color for event type
  const getCouleurByType = (type) => {
    switch (type) {
      case 'Départ':
        return 'success';
      case 'Retour':
        return 'primary';
      case 'Professionnel':
        return 'info';
      case 'Formation':
        return 'secondary';
      case 'Événement':
        return 'error';
      default:
        return 'default';
    }
  };

  // Get icon for event type
  const getTypeIcon = (type) => {
    switch (type) {
      case 'Départ':
        return <FlightIcon sx={{ fontSize: 16 }} />;
      case 'Retour':
        return <LandingIcon sx={{ fontSize: 16 }} />;
      case 'Professionnel':
        return <BusinessIcon sx={{ fontSize: 16 }} />;
      case 'Formation':
        return <SchoolIcon sx={{ fontSize: 16 }} />;
      case 'Événement':
        return <EventIcon sx={{ fontSize: 16 }} />;
      default:
        return <EventIcon sx={{ fontSize: 16 }} />;
    }
  };

  const calendarDays = generateCalendarDays();
  const monthEvents = getEventsForMonth();

  // Enhanced statistics for real-time data
  const statsCards = React.useMemo(() => {
    // Always return proper cards structure, but with placeholder values if loading
    // This allows the UI to render with proper layout while data loads
    if (isLoading) {
      return [
        { title: 'Événements du Mois', value: '...', icon: EventIcon, color: 'primary', trend: '...', subtitle: 'Chargement...' },
        { title: 'Clients Voyageant', value: '...', icon: PersonIcon, color: 'info', trend: '...', subtitle: 'Chargement...' },
        { title: 'Revenu Estimé', value: '...', icon: MoneyIcon, color: 'success', trend: '...', subtitle: 'Chargement...' },
        { title: 'Destinations', value: '...', icon: LocationIcon, color: 'warning', trend: '...', subtitle: 'Chargement...' }
      ];
    }
    
    // Calculate clients with upcoming trips
    const clientsWithTrips = [...new Set((allEvents || [])
      .filter(e => e.client && e.type === 'Départ' && new Date(e.date) >= new Date())
      .map(e => e.client.id)
    )].length;
    
    // Calculate total revenue from events this month
    const currentMonth = new Date().getMonth();
    const currentYear = new Date().getFullYear();
    const monthlyRevenue = (allEvents || [])
      .filter(e => {
        const eventDate = new Date(e.date);
        return eventDate.getMonth() === currentMonth && eventDate.getFullYear() === currentYear;
      })
      .reduce((sum, e) => sum + (e.revenue || 0), 0);
    
    // Calculate unique destinations
    const uniqueDestinations = [...new Set((allEvents || [])
      .filter(e => e.destination)
      .map(e => {
        const parts = e.destination.split(',');
        return parts.length > 1 ? parts[1].trim() : parts[0].trim();
      })
    )];
    
    // Calculate revenue trend compared to previous month
    const previousMonthRevenue = financialData
      ? financialData
          .filter(t => {
            const transDate = new Date(t.date);
            const prevMonth = currentMonth === 0 ? 11 : currentMonth - 1;
            const prevYear = currentMonth === 0 ? currentYear - 1 : currentYear;
            return transDate.getMonth() === prevMonth && transDate.getFullYear() === prevYear;
          })
          .reduce((sum, t) => sum + (t.montant || 0), 0)
      : 0;
    
    const revenueTrend = previousMonthRevenue > 0 
      ? `${Math.round((monthlyRevenue / previousMonthRevenue - 1) * 100)}%`
      : '+N/A';
    
    return [
      {
        title: 'Événements du Mois',
        value: eventsStats?.totalEvents || 0,
        icon: EventIcon,
      color: 'primary',
        trend: `+${Math.round(eventsStats?.upcomingEvents / (eventsStats?.totalEvents || 1) * 100)}%`,
        subtitle: `${eventsStats?.upcomingEvents || 0} à venir`
      },
      {
        title: 'Clients Voyageant',
        value: clientsWithTrips,
        icon: PersonIcon,
        color: 'info',
        trend: `${Math.round(clientsWithTrips / ((clients && clients.length) || 1) * 100)}%`,
        subtitle: 'Clients actifs'
      },
      {
        title: 'Revenu Estimé',
        value: formatCurrency(monthlyRevenue),
        icon: MoneyIcon,
        color: 'success',
        trend: revenueTrend,
        isCurrency: true,
        subtitle: 'Ce mois'
      },
      {
        title: 'Destinations',
        value: uniqueDestinations.length,
        icon: LocationIcon,
      color: 'warning',
        trend: `+${uniqueDestinations.length - 3}`,
        subtitle: 'Pays desservis'
    }
  ];
  }, [allEvents, eventsStats, clients, financialData, isLoading, formatCurrency]);

  // Data for charts based on real-time data
  const eventTypeData = [
    { label: 'Départs', value: eventsStats?.eventsByType?.['Départ'] || 0, color: 'success' },
    { label: 'Retours', value: eventsStats?.eventsByType?.['Retour'] || 0, color: 'primary' },
    { label: 'Professionnels', value: eventsStats?.eventsByType?.['Professionnel'] || 0, color: 'info' },
    { label: 'Formations', value: eventsStats?.eventsByType?.['Formation'] || 0, color: 'secondary' },
    { label: 'Événements', value: eventsStats?.eventsByType?.['Événement'] || 0, color: 'error' }
  ].filter(item => item.value > 0);
  
  // Revenue data by destination
  const revenueData = React.useMemo(() => {
    if (isLoading || !allEvents || (allEvents && allEvents.length === 0)) {
      return [];
    }
    
    return Object.entries(allEvents
      .reduce((acc, event) => {
        if (event.revenue > 0 && event.destination) {
          const destination = event.destination.split(',')[0].trim();
          acc[destination] = (acc[destination] || 0) + event.revenue;
        }
        return acc;
      }, {}))
      .map(([key, value]) => ({ 
        label: key, 
        value: value, 
        color: ['primary', 'success', 'info', 'warning', 'error'][Math.floor(Math.random() * 5)] 
      }))
      .slice(0, 5);
  }, [allEvents, isLoading]);

  // Weekly activity data - use real events
  const weeklyActivityData = React.useMemo(() => {
    if (isLoading || !allEvents || (allEvents && allEvents.length === 0)) {
      return Array(7).fill(0);
    }
    
    // Get current week dates
    const today = new Date();
    const currentDay = today.getDay(); // 0 = Sunday, 1 = Monday, etc.
    const weekDays = Array(7).fill(0).map((_, i) => {
      const date = new Date(today);
      // Adjust to get Monday as first day (0)
      const dayDiff = i - ((currentDay || 7) - 1);
      date.setDate(date.getDate() + dayDiff);
      return date.toISOString().split('T')[0];
    });
    
    // Count events for each day of the week
    return weekDays.map(dateStr => 
      (allEvents || []).filter(e => e.date === dateStr).length
    );
  }, [allEvents, isLoading]);

  // Define SkeletonCard component for progressive loading
  const SkeletonCard = ({ height = 120 }) => (
    <Skeleton variant="rectangular" height={height} sx={{ borderRadius: 3, width: '100%' }} />
  );
  
  // Define SkeletonEvent component for calendar events
  const SkeletonEvent = () => (
    <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
      <Skeleton variant="circular" width={16} height={16} sx={{ mr: 0.5, bgcolor: 'rgba(255, 255, 255, 0.2)' }} />
      <Skeleton variant="rectangular" width="100%" height={18} sx={{ borderRadius: 10, bgcolor: 'rgba(255, 255, 255, 0.2)' }} />
    </Box>
  );
  
  // We're using progressive loading, so we don't need a full loading screen

  // Error state
  if (eventsError) {
    return (
      <Box sx={{ p: { xs: 2, md: 3 } }}>
        <Typography variant="h3" sx={{ fontWeight: 800, mb: 2, fontSize: { xs: '1.8rem', sm: '2.2rem', md: '2.5rem' } }}>
          Calendrier
        </Typography>
        <Alert severity="error" sx={{ mb: 4, borderRadius: 2 }}>
          Une erreur est survenue lors du chargement des données : {eventsError}
        </Alert>
        <Button 
          variant="contained" 
          onClick={() => window.location.reload()}
          sx={{
            borderRadius: 2,
            background: 'linear-gradient(135deg, #1e40af 0%, #7c3aed 100%)',
            color: 'white',
            px: 4, 
            py: 1,
            '&:hover': {
              background: 'linear-gradient(135deg, #1e3a8a 0%, #6d28d9 100%)'
            }
          }}
        >
          Réessayer
        </Button>
      </Box>
    );
  }

  const renderTabContent = () => {
    switch (selectedTab) {
      case 0:
        return (
          <Grid container spacing={3} sx={{ px: { xs: 2, md: 0 } }}>
            {/* Calendar Section */}
            <Grid item xs={12} lg={8}>
              <Card 
                sx={{
                  background: 'rgba(255, 255, 255, 0.1)',
                  backdropFilter: 'blur(20px)',
                  borderRadius: 3,
                  border: '1px solid rgba(255, 255, 255, 0.2)'
                }}
              >
                <CardContent>
                  {/* Calendar Header */}
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <IconButton onClick={() => navigateMonth(-1)} sx={{ color: 'white' }}>
                        <ChevronLeftIcon />
                      </IconButton>
                      <Typography variant="h5" sx={{ fontWeight: 600, minWidth: 200, textAlign: 'center', color: 'white' }}>
                        {currentDate.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' })}
                      </Typography>
                      <IconButton onClick={() => navigateMonth(1)} sx={{ color: 'white' }}>
                        <ChevronRightIcon />
                      </IconButton>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Button
                        variant="outlined"
                        startIcon={<TodayIcon />}
                        onClick={goToToday}
                        sx={{ 
                          borderRadius: 2, 
                          color: 'white', 
                          borderColor: 'rgba(255, 255, 255, 0.3)',
                          '&:hover': { 
                            borderColor: 'white', 
                            backgroundColor: 'rgba(255, 255, 255, 0.1)'
                          }
                        }}
                      >
                        Aujourd'hui
                      </Button>
                      <ToggleButtonGroup
                        value={viewMode}
                        exclusive
                        onChange={(e, newMode) => newMode && setViewMode(newMode)}
                        size="small"
                        sx={{ 
                          '& .MuiToggleButton-root': { 
                            color: 'rgba(255, 255, 255, 0.7)', 
                            borderColor: 'rgba(255, 255, 255, 0.3)',
                            '&:hover': {
                              backgroundColor: 'rgba(255, 255, 255, 0.1)',
                              color: 'white'
                            }
                          },
                          '& .Mui-selected': { 
                            backgroundColor: 'linear-gradient(135deg, #1e40af 0%, #7c3aed 100%) !important',
                            background: 'linear-gradient(135deg, #1e40af 0%, #7c3aed 100%) !important',
                            color: 'white !important',
                            borderColor: 'rgba(30, 64, 175, 0.5) !important',
                            '&:hover': {
                              backgroundColor: 'linear-gradient(135deg, #1e3a8a 0%, #6d28d9 100%) !important',
                              background: 'linear-gradient(135deg, #1e3a8a 0%, #6d28d9 100%) !important'
                            }
                          }
                        }}
                      >
                        <ToggleButton value="month">
                          <MonthIcon />
                        </ToggleButton>
                        <ToggleButton value="list">
                          <ViewListIcon />
                        </ToggleButton>
                      </ToggleButtonGroup>
                      
                      {/* Table/Card View Toggle - Only show in list mode */}
                      {viewMode === 'list' && (
                        <Box sx={{ ml: 2 }}>
                          <ToggleButtonGroup
                            value={displayMode}
                            exclusive
                            onChange={(e, newMode) => {
                              if (newMode !== null) {
                                setDisplayMode(newMode);
                              }
                            }}
                            size="small"
                            sx={{ 
                              '& .MuiToggleButton-root': { 
                                color: 'rgba(255, 255, 255, 0.7)', 
                                borderColor: 'rgba(255, 255, 255, 0.3)',
                                '&:hover': {
                                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                                  color: 'white'
                                }
                              },
                              '& .Mui-selected': { 
                                backgroundColor: 'linear-gradient(135deg, #1e40af 0%, #7c3aed 100%) !important',
                                background: 'linear-gradient(135deg, #1e40af 0%, #7c3aed 100%) !important',
                                color: 'white !important',
                                borderColor: 'rgba(30, 64, 175, 0.5) !important',
                                '&:hover': {
                                  backgroundColor: 'linear-gradient(135deg, #1e3a8a 0%, #6d28d9 100%) !important',
                                  background: 'linear-gradient(135deg, #1e3a8a 0%, #6d28d9 100%) !important'
                                }
                              }
                            }}
                          >
                            <ToggleButton value="table">
                              <TableViewIcon />
                            </ToggleButton>
                            <ToggleButton value="card">
                              <CardViewIcon />
                            </ToggleButton>
                          </ToggleButtonGroup>
                        </Box>
                      )}
                    </Box>
                  </Box>

                  {/* Calendar View - Month or List */}
                  {viewMode === 'month' ? (
                  <Box sx={{ position: 'relative' }}>
                    {/* Subtle loading overlay */}
                    {isLoading && (
                      <Box sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        bgcolor: 'rgba(0,0,0,0.4)',
                        zIndex: 1,
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        borderRadius: 2
                      }}>
                        <CircularProgress size={40} />
                      </Box>
                    )}
                    {/* Days of week header */}
                    <Grid container>
                      {['Dim', 'Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam'].map((day) => (
                        <Grid item xs={12/7} key={day}>
                          <Box sx={{ 
                            p: 1, 
                            textAlign: 'center', 
                            fontWeight: 600, 
                            color: 'rgba(255, 255, 255, 0.7)', 
                            borderBottom: '1px solid rgba(255, 255, 255, 0.2)' 
                          }}>
                            {day}
                          </Box>
                        </Grid>
                      ))}
                    </Grid>

                    {/* Calendar days */}
                    <Grid container>
                      {calendarDays.map((day, index) => {
                        const dayEvents = getEventsForDate(day);
                        const isCurrentMonth = day.getMonth() === currentDate.getMonth();
                        const isToday = day.toDateString() === new Date().toDateString();
                        const hasRevenue = dayEvents.some(e => e.revenue > 0);
                        
                        return (
                          <Grid item xs={12/7} key={index}>
                            <Box
                              onClick={() => handleOpenDialog(null, day)}
                              sx={{
                                minHeight: 120,
                                p: 1,
                                position: 'relative',
                                cursor: 'pointer',
                                borderRadius: 3,
                                border: '1px solid rgba(255, 255, 255, 0.2)',
                                background: isToday 
                                  ? 'linear-gradient(135deg, rgba(99, 102, 241, 0.3), rgba(139, 92, 246, 0.3))'
                                  : 'rgba(255, 255, 255, 0.1)',
                                backdropFilter: 'blur(20px)',
                                transition: 'all 0.3s ease',
                                '&:hover': {
                                  background: isToday 
                                    ? 'linear-gradient(135deg, rgba(99, 102, 241, 0.4), rgba(139, 92, 246, 0.4))'
                                    : 'rgba(255, 255, 255, 0.15)'
                                }
                              }}
                            >
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                <Typography 
                                  variant="body2" 
                                  sx={{ 
                                    color: isCurrentMonth ? 'white' : 'rgba(255, 255, 255, 0.4)',
                                    fontWeight: isToday ? 'bold' : 'medium'
                                  }}
                                >
                                  {day.getDate()}
                                </Typography>
                                {dayEvents.length > 0 && (
                                  <Stack direction="row" spacing={0.5}>
                                    <Badge badgeContent={dayEvents.length} color="primary" />
                                    {hasRevenue && (
                                      <MoneyIcon sx={{ fontSize: 16, color: 'success.main' }} />
                                    )}
                                  </Stack>
                                )}
                              </Box>
                              
                              {/* Event indicators */}
                              <Box sx={{ mt: 1 }}>
                                {isLoading ? (
                                  // Show skeleton events while loading
                                  Array(Math.floor(Math.random() * 2) + 1).fill(0).map((_, idx) => (
                                    <SkeletonEvent key={idx} />
                                  ))
                                ) : dayEvents.slice(0, 3).map((event, idx) => (
                                  <Box key={idx} sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                                    {event.client && (
                                      <Avatar 
                                        sx={{ width: 16, height: 16, mr: 0.5 }}
                                        src={event.client.avatar}
                                        >
                                          {event.client.prenom?.charAt(0)}
                                        </Avatar>
                                    )}
                                    <Chip
                                      label={event.titre}
                                      size="small"
                                      color={getCouleurByType(event.type)}
                                      sx={{ 
                                        maxWidth: '100%',
                                        height: 18,
                                        fontSize: '0.65rem',
                                        '& .MuiChip-label': {
                                          overflow: 'hidden',
                                          textOverflow: 'ellipsis',
                                          whiteSpace: 'nowrap'
                                        }
                                      }}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleOpenDialog(event);
                                      }}
                                    />
                                  </Box>
                                ))}
                                {!isLoading && dayEvents.length > 3 && (
                                  <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                                    +{dayEvents.length - 3} autres
                                  </Typography>
                                )}
                              </Box>
                            </Box>
                          </Grid>
                        );
                      })}
                    </Grid>
                  </Box>
                  ) : (
                    // List View
                    <Box>
                      <List sx={{ width: '100%' }}>
                        {isLoading ? (
                          // Show skeleton list items while loading
                          Array(5).fill(0).map((_, idx) => (
                            <ListItem key={idx} sx={{ 
                              px: 2, 
                              py: 1.5, 
                              mb: 1, 
                              borderRadius: 1,
                              backgroundColor: 'rgba(255, 255, 255, 0.05)'
                            }}>
                              <ListItemAvatar>
                                <Skeleton variant="circular" width={40} height={40} sx={{ bgcolor: 'rgba(255, 255, 255, 0.1)' }} />
                              </ListItemAvatar>
                              <ListItemText
                                primary={<Skeleton width="60%" sx={{ bgcolor: 'rgba(255, 255, 255, 0.1)' }} />}
                                secondary={
                                  <Box>
                                    <Skeleton width="40%" sx={{ bgcolor: 'rgba(255, 255, 255, 0.1)' }} />
                                    <Skeleton width="30%" sx={{ mt: 0.5, bgcolor: 'rgba(255, 255, 255, 0.1)' }} />
                                  </Box>
                                }
                              />
                            </ListItem>
                          ))
                        ) : monthEvents.length === 0 ? (
                          <Box sx={{ textAlign: 'center', py: 4 }}>
                            <Typography variant="body1" color="rgba(255, 255, 255, 0.6)">
                              Aucun événement ce mois-ci
                            </Typography>
                            <Button 
                              variant="contained" 
                              startIcon={<AddIcon />} 
                              sx={{ 
                                mt: 2,
                                background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
                                color: 'white',
                                '&:hover': {
                                  background: 'linear-gradient(135deg, #4f46e5, #7c3aed)'
                                }
                              }}
                              onClick={() => handleOpenDialog()}
                            >
                              Ajouter un événement
                            </Button>
                          </Box>
                        ) : (
                          monthEvents
                            .sort((a, b) => new Date(a.date) - new Date(b.date))
                            .map((event, index) => {
                              const eventDate = new Date(event.date);
                              const isToday = eventDate.toDateString() === new Date().toDateString();
                              const isPast = eventDate < new Date();
                              
                              return (
                                <React.Fragment key={event.id || index}>
                                  <ListItem 
                                    className="bg-white/10 backdrop-blur-lg rounded-xl border border-white/20 mb-2"
                                    sx={{
                                      '&:hover': {
                                        backgroundColor: 'rgba(255, 255, 255, 0.15) !important',
                                      }
                                    }}
                                    secondaryAction={
                                      <IconButton 
                                        edge="end" 
                                        onClick={() => handleOpenDialog(event)}
                                        sx={{ color: 'white' }}
                                      >
                                        <EditIcon />
                                      </IconButton>
                                    }
                                  >
                                    <ListItemAvatar>
                                      <Avatar 
                                        sx={{ 
                                          bgcolor: `${event.couleur || getCouleurByType(event.type)}.main` 
                                        }}
                                      >
                                        {getTypeIcon(event.type)}
                                      </Avatar>
                                    </ListItemAvatar>
                                    <ListItemText
                                      primary={
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                          <Typography variant="subtitle2" sx={{ color: 'white', fontWeight: 600 }}>
                                            {event.titre}
                                          </Typography>
                                          {event.client && (
                                            <Chip 
                                              label={`${event.client.prenom} ${event.client.nom}`} 
                                              size="small" 
                                              variant="outlined"
                                              sx={{ borderColor: 'rgba(255,255,255,0.3)', color: 'rgba(255,255,255,0.9)' }}
                                            />
                                          )}
                                        </Box>
                                      }
                                      secondary={
                                        <Box sx={{ mt: 0.5 }}>
                                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                              <CalendarTodayIcon sx={{ fontSize: 14, color: 'rgba(255,255,255,0.7)' }} />
                                              <Typography variant="body2" component="span" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                                                {formatDate(eventDate)}
                                              </Typography>
                                            </Box>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                              <TimeIcon sx={{ fontSize: 14, color: 'rgba(255,255,255,0.7)' }} />
                                              <Typography variant="body2" component="span" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                                                {event.heure}
                                              </Typography>
                                            </Box>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                              <LocationIcon sx={{ fontSize: 14, color: 'rgba(255,255,255,0.7)' }} />
                                              <Typography variant="body2" component="span" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                                                {event.destination}
                                              </Typography>
                                            </Box>
                                          </Box>
                                          {event.revenue > 0 && (
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                                              <MoneyIcon sx={{ fontSize: 14, color: '#4ade80' }} />
                                              <Typography variant="body2" component="span" sx={{ color: '#4ade80', fontWeight: 500 }}>
                                                {formatCurrency(event.revenue)}
                                              </Typography>
                                            </Box>
                                          )}
                                        </Box>
                                      }
                                    />
                                  </ListItem>
                                </React.Fragment>
                              );
                            })
                        )}
                      </List>
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Grid>

            {/* Events Sidebar */}
            <Grid item xs={12} lg={4}>
              <Card 
                sx={{ 
                  borderRadius: 3,
                  background: 'rgba(255, 255, 255, 0.1)',
                  backdropFilter: 'blur(20px)',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                  mb: 3,
                  position: 'relative'
                }}
              >
                {isLoading && (
                  <LinearProgress 
                    sx={{ 
                      position: 'absolute', 
                      top: 0, 
                      left: 0, 
                      right: 0, 
                      height: 3, 
                      borderTopLeftRadius: 3,
                      borderTopRightRadius: 3
                    }} 
                  />
                )}
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h6" sx={{ fontWeight: 600, color: 'white' }}>
                      Événements à venir
                    </Typography>
                    <IconButton
                      color="primary"
                      onClick={() => handleOpenDialog()}
                      sx={{ 
                        bgcolor: 'primary.main',
                        color: 'white',
                        '&:hover': { bgcolor: 'primary.dark' }
                      }}
                    >
                      <AddIcon />
                    </IconButton>
                  </Box>
                  
                  <List>
                    {isLoading ? (
                      // Show skeleton list items while loading
                      Array(4).fill(0).map((_, idx) => (
                        <React.Fragment key={idx}>
                          <ListItem sx={{ px: 0 }}>
                            <ListItemAvatar>
                              <Skeleton variant="circular" width={40} height={40} sx={{ bgcolor: 'rgba(255, 255, 255, 0.2)' }} />
                            </ListItemAvatar>
                            <ListItemText
                              primary={<Skeleton width="60%" sx={{ bgcolor: 'rgba(255, 255, 255, 0.2)' }} />}
                              secondary={
                                <Box>
                                  <Skeleton width="40%" sx={{ bgcolor: 'rgba(255, 255, 255, 0.2)' }} />
                                  <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
                                    <Skeleton width={60} height={20} sx={{ bgcolor: 'rgba(255, 255, 255, 0.2)' }} />
                                    <Skeleton width={60} height={20} sx={{ bgcolor: 'rgba(255, 255, 255, 0.2)' }} />
                                  </Box>
                                </Box>
                              }
                            />
                          </ListItem>
                          {idx < 3 && <Divider sx={{ bgcolor: 'rgba(255, 255, 255, 0.2)' }} />}
                        </React.Fragment>
                      ))
                    ) : allEvents
                      .filter(event => new Date(event.date) >= new Date())
                      .sort((a, b) => new Date(a.date) - new Date(b.date))
                      .slice(0, 6)
                      .map((event, index) => (
                      <React.Fragment key={event.id}>
                        <ListItem 
                          sx={{ 
                            px: 0,
                            '&:hover': { 
                              backgroundColor: 'rgba(30, 64, 175, 0.04)',
                              borderRadius: 2
                            }
                          }}
                        >
                          <ListItemAvatar>
                            <Avatar 
                              sx={{ 
                                bgcolor: `${event.couleur || getCouleurByType(event.type)}.main`,
                                width: 40,
                                height: 40
                              }}
                            >
                              {getTypeIcon(event.type)}
                            </Avatar>
                          </ListItemAvatar>
                          <ListItemText
                            primary={
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'white' }}>
                                  {event.titre}
                                </Typography>
                                {event.client && (
                                  <Avatar 
                                    sx={{ width: 20, height: 20 }}
                                    src={event.client.avatar}
                                  >
                                    {event.client.prenom?.charAt(0)}
                                  </Avatar>
                                )}
                              </Box>
                            }
                            secondary={
                              <Box>
                                <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                                  {formatDate(new Date(event.date))} à {event.heure}
                                </Typography>
                                <br />
                                <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
                                  <Chip
                                    label={event.statut}
                                    size="small"
                                    color={event.couleur || getCouleurByType(event.type)}
                                    variant="outlined"
                                    sx={{ borderRadius: 2 }}
                                  />
                                  {event.revenue > 0 && (
                                    <Chip
                                      label={formatCurrency(event.revenue)}
                                      size="small"
                                      color="success"
                                      variant="outlined"
                                      sx={{ borderRadius: 2 }}
                                    />
                                  )}
                                </Box>
                              </Box>
                            }
                          />
                          <IconButton
                            size="small"
                            onClick={() => handleOpenDialog(event)}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={(e) => {
                              e.stopPropagation();
                              setEditingEvent(event);
                              handleDeleteEvent();
                            }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </ListItem>
                        {index < 5 && <Divider sx={{ bgcolor: 'rgba(255, 255, 255, 0.2)' }} />}
                      </React.Fragment>
                    ))}
                  </List>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        );
      case 1:
        return (
          <Grid container spacing={3} sx={{ px: { xs: 2, md: 0 } }}>
            <Grid item xs={12} md={4}>
              {isLoading ? (
                <Card sx={{ 
                  mb: 2, 
                  borderRadius: 3, 
                  background: 'rgba(255, 255, 255, 0.1)',
                  backdropFilter: 'blur(20px)',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  boxShadow: '0 4px 20px rgba(0,0,0,0.08)' 
                }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                      <PieChartIcon sx={{ color: 'rgba(255, 255, 255, 0.7)' }} />
                      <Skeleton width={180} sx={{ bgcolor: 'rgba(255, 255, 255, 0.2)' }} />
                    </Box>
                    <Box sx={{ mt: 2 }}>
                      {Array(4).fill(0).map((_, idx) => (
                        <Box key={idx} sx={{ mb: 1 }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                            <Skeleton width={100} sx={{ bgcolor: 'rgba(255, 255, 255, 0.2)' }} />
                            <Skeleton width={40} sx={{ bgcolor: 'rgba(255, 255, 255, 0.2)' }} />
                          </Box>
                          <Skeleton height={8} sx={{ borderRadius: 1, bgcolor: 'rgba(255, 255, 255, 0.2)' }} />
                        </Box>
                      ))}
                    </Box>
                  </CardContent>
                </Card>
              ) : (
                <SimpleEventChart data={eventTypeData} title="Répartition des Événements" />
              )}
            </Grid>
            <Grid item xs={12} md={4}>
              {isLoading ? (
                <Card sx={{ 
                  mb: 2, 
                  borderRadius: 3, 
                  background: 'rgba(255, 255, 255, 0.1)',
                  backdropFilter: 'blur(20px)',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  boxShadow: '0 4px 20px rgba(0,0,0,0.08)' 
                }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                      <PieChartIcon sx={{ color: 'rgba(255, 255, 255, 0.7)' }} />
                      <Skeleton width={180} sx={{ bgcolor: 'rgba(255, 255, 255, 0.2)' }} />
                    </Box>
                    <Box sx={{ mt: 2 }}>
                      {Array(4).fill(0).map((_, idx) => (
                        <Box key={idx} sx={{ mb: 1 }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                            <Skeleton width={100} sx={{ bgcolor: 'rgba(255, 255, 255, 0.2)' }} />
                            <Skeleton width={40} sx={{ bgcolor: 'rgba(255, 255, 255, 0.2)' }} />
                          </Box>
                          <Skeleton height={8} sx={{ borderRadius: 1, bgcolor: 'rgba(255, 255, 255, 0.2)' }} />
                        </Box>
                      ))}
                    </Box>
                  </CardContent>
                </Card>
              ) : (
                <SimpleEventChart data={revenueData} title="Revenus par Destination" />
              )}
            </Grid>
            <Grid item xs={12} md={4}>
              {isLoading ? (
                <Card sx={{ 
                  mb: 2, 
                  borderRadius: 3, 
                  background: 'rgba(255, 255, 255, 0.1)',
                  backdropFilter: 'blur(20px)',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  boxShadow: '0 4px 20px rgba(0,0,0,0.08)' 
                }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                      <BarChartIcon sx={{ color: 'rgba(255, 255, 255, 0.7)' }} />
                      <Skeleton width={180} sx={{ bgcolor: 'rgba(255, 255, 255, 0.2)' }} />
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'end', gap: 1, height: 100, mt: 2 }}>
                      {Array(7).fill(0).map((_, idx) => (
                        <Box key={idx} sx={{ flex: 1, textAlign: 'center' }}>
                          <Skeleton height={`${Math.floor(Math.random() * 70) + 10}px`} sx={{ borderRadius: 1, mb: 1, bgcolor: 'rgba(255, 255, 255, 0.2)' }} />
                          <Skeleton width={20} height={15} sx={{ mx: 'auto', bgcolor: 'rgba(255, 255, 255, 0.2)' }} />
                        </Box>
                      ))}
                    </Box>
                  </CardContent>
                </Card>
              ) : (
                <SimpleActivityChart activities={weeklyActivityData} />
              )}
            </Grid>
          </Grid>
        );
      default:
        return null;
    }
  };

  return (
    <Box sx={{ p: { xs: 0, md: 3 } }} className="space-y-6">
      {/* Header Section */}
      <Box sx={{ 
        mb: { xs: 2, md: 4 },
        p: { xs: 2, md: 0 }
      }} className="flex justify-between items-center">
        <div>
          <Typography variant="h3" sx={{ 
            fontWeight: 800, 
            color: 'white',
            mb: 1.5,
            fontSize: { xs: '1.8rem', sm: '2.2rem', md: '2.5rem' },
            letterSpacing: '-0.01em'
          }}>
            Calendrier
          </Typography>
          <Typography variant="body1" sx={{ 
            color: 'rgba(255,255,255,0.6)',
            fontSize: { xs: '1rem', sm: '1.1rem', md: '1.2rem' }
          }}>
            Planification avancée avec données clients et revenus en temps réel
          </Typography>
        </div>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<ExportIcon />}
            onClick={exportCalendrierToPDF}
            sx={{ 
              borderRadius: 2,
              color: 'white',
              borderColor: 'rgba(255, 255, 255, 0.3)',
              '&:hover': {
                borderColor: 'rgba(255, 255, 255, 0.5)',
                backgroundColor: 'rgba(255, 255, 255, 0.1)'
              }
            }}
          >
            Exporter PDF
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
            sx={{ 
              borderRadius: 2,
              background: 'linear-gradient(135deg, #1e40af 0%, #7c3aed 100%)',
              color: 'white',
              px: 3,
              py: 1.5,
              '&:hover': {
                background: 'linear-gradient(135deg, #1e3a8a 0%, #6d28d9 100%)'
              }
            }}
          >
            Nouvel Événement
          </Button>
        </Box>
        
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20 mt-4 flex items-center justify-between"
        >
          <div className="flex items-center gap-3">
            <LinkIcon sx={{ color: '#f59e0b' }} />
            <div>
              <Typography variant="subtitle2" sx={{ color: 'white', fontWeight: 600 }}>
                Calendrier Interconnecté Actif
              </Typography>
              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.6)' }}>
                Synchronisation en temps réel avec {(allEvents && allEvents.length) || 0} événements
              </Typography>
            </div>
          </div>
          <span className="bg-green-600 text-white text-xs px-3 py-1 rounded-full font-medium">
            Connecté
          </span>
        </motion.div>
      </Box>
      
      {/* Quick Actions Section */}
      {showQuickActions && (
      <Box sx={{ mb: { xs: 3, md: 4 }, px: { xs: 2, md: 0 } }} className="space-y-2">
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <RocketLaunch sx={{ mr: 1.5, color: '#f59e0b', fontSize: { xs: 26, md: 30 } }} />
          <Typography variant="h5" sx={{ fontWeight: 700, fontSize: { xs: '1.4rem', md: '1.6rem' }, color: 'white' }}>
            Actions Rapides
          </Typography>
        </Box>
          
          <Grid container spacing={{ xs: 2, md: 3 }}>
            <Grid item xs={6} sm={6} md={3}>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20 h-full cursor-pointer hover:bg-white/15 transition-all duration-300"
              onClick={() => handleOpenDialog()}
            >
              <div className="flex items-center gap-4 mb-3">
                <div className="p-3 rounded-2xl bg-gradient-to-r from-blue-600 to-blue-800">
                  <EventIcon className="h-6 w-6 text-white" />
                </div>
              </div>
              <h3 className="text-lg font-bold text-white mb-1">Nouvel Événement</h3>
              <p className="text-white/60 text-sm">Ajouter un événement au calendrier</p>
            </motion.div>
          </Grid>
            <Grid item xs={6} sm={6} md={3}>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20 h-full cursor-pointer hover:bg-white/15 transition-all duration-300"
              onClick={() => {
                setFormData({
                  ...formData,
                  type: 'Départ',
                  date: new Date().toISOString().split('T')[0]
                });
                handleOpenDialog();
              }}
            >
              <div className="flex items-center gap-4 mb-3">
                <div className="p-3 rounded-2xl bg-gradient-to-r from-green-600 to-green-800">
                  <FlightIcon className="h-6 w-6 text-white" />
                </div>
              </div>
              <h3 className="text-lg font-bold text-white mb-1">Créer Départ</h3>
              <p className="text-white/60 text-sm">Planifier un départ de voyage</p>
            </motion.div>
          </Grid>
            <Grid item xs={6} sm={6} md={3}>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20 h-full cursor-pointer hover:bg-white/15 transition-all duration-300"
              onClick={exportCalendrierToPDF}
            >
              <div className="flex items-center gap-4 mb-3">
                <div className="p-3 rounded-2xl bg-gradient-to-r from-green-600 to-green-800">
                  <PdfIcon className="h-6 w-6 text-white" />
                </div>
              </div>
              <h3 className="text-lg font-bold text-white mb-1">Exporter PDF</h3>
              <p className="text-white/60 text-sm">Générer un rapport du calendrier</p>
            </motion.div>
          </Grid>
            <Grid item xs={6} sm={6} md={3}>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20 h-full cursor-pointer hover:bg-white/15 transition-all duration-300"
              onClick={() => setViewMode('list')}
            >
              <div className="flex items-center gap-4 mb-3">
                <div className="p-3 rounded-2xl bg-gradient-to-r from-purple-600 to-purple-800">
                  <ViewListIcon className="h-6 w-6 text-white" />
                </div>
              </div>
              <h3 className="text-lg font-bold text-white mb-1">Vue par Liste</h3>
              <p className="text-white/60 text-sm">Afficher la liste d'événements</p>
            </motion.div>
                    </Grid>
        </Grid>
      </Box>
      )}

      {/* Key Metrics Section */}
      <Box sx={{ mb: { xs: 3, md: 4 }, px: { xs: 2, md: 0 } }} className="space-y-2">
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <AutoAwesome sx={{ mr: 1.5, color: '#10b981', fontSize: { xs: 26, md: 30 } }} />
          <Typography variant="h5" sx={{ fontWeight: 700, fontSize: { xs: '1.4rem', md: '1.6rem' }, color: 'white' }}>
            Métriques en Temps Réel
          </Typography>
        </Box>
          
          <Grid container spacing={{ xs: 2, md: 3 }}>
            {statsCards.map((stat, index) => (
              <Grid item xs={12} sm={6} md={3} key={index}>
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.4 + index * 0.1 }}
                >
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                    className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20 h-full"
                  >
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="text-white/60 text-sm font-medium">{stat.title}</p>
                        <p className="text-2xl font-bold text-white mt-2">
                          {stat.isCurrency ? stat.value : stat.value}
                        </p>
                        {stat.subtitle && (
                          <p className="text-white/60 text-sm mt-1">{stat.subtitle}</p>
                        )}
                        {stat.trend && (
                          <span className={`inline-flex items-center px-2 py-0.5 mt-2 rounded-full text-xs font-medium ${
                            stat.trend.startsWith('+') ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                          }`}>
                            {stat.trend}
                          </span>
                        )}
                      </div>
                      <div className={`p-3 rounded-2xl bg-gradient-to-r ${
                        index === 0 ? 'from-blue-600 to-indigo-600' : 
                        index === 1 ? 'from-green-600 to-green-800' : 
                        index === 2 ? 'from-amber-500 to-amber-700' : 
                        'from-purple-600 to-purple-800'
                      }`}>
                        <stat.icon className="h-6 w-6 text-white" />
                      </div>
                    </div>
                  </motion.div>
                </motion.div>
              </Grid>
            ))}
          </Grid>
      </Box>

      {/* Tabs Navigation */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.5 }}
        className="bg-white/10 backdrop-blur-lg rounded-3xl border border-white/20 mb-6 mx-2 md:mx-0 relative overflow-hidden"
      >
          {isLoading && (
            <LinearProgress 
              sx={{ 
                position: 'absolute', 
                top: 0, 
                left: 0, 
                right: 0, 
                height: 3, 
                borderTopLeftRadius: 3,
                borderTopRightRadius: 3
              }} 
            />
          )}
          <div className="flex space-x-1 overflow-x-auto hide-scrollbar p-2">
            {[{ id: 0, name: 'Calendrier', icon: CalendarTodayIcon }, { id: 1, name: 'Analytiques', icon: AssessmentIcon }].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setSelectedTab(tab.id)}
                className={`flex-1 flex items-center justify-center space-x-2 px-4 py-3 rounded-2xl font-medium transition-all duration-300 ${
                  selectedTab === tab.id
                    ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white shadow-lg'
                    : 'text-white/80 hover:bg-white/10 hover:text-white'
                }`}
              >
                <tab.icon className="h-5 w-5" />
                <span className="whitespace-nowrap">{tab.name}</span>
              </button>
            ))}
          </div>
        </motion.div>

      {/* Tab Content */}
      {renderTabContent()}

      {/* Floating Action Button */}
      <Fab
        color="primary"
        aria-label="add"
        onClick={() => handleOpenDialog()}
        sx={{
          position: 'fixed',
          bottom: 32,
          right: 32,
          background: 'linear-gradient(135deg, #1e40af 0%, #7c3aed 100%)',
          color: 'white',
          boxShadow: '0 4px 20px rgba(0,0,0,0.2)',
          '&:hover': {
            background: 'linear-gradient(135deg, #1e3a8a 0%, #6d28d9 100%)'
          }
        }}
      >
        <AddIcon />
      </Fab>

      {/* Enhanced Add/Edit Event Dialog */}
      <Dialog 
        open={openDialog} 
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
        className="modern-dialog"
        PaperProps={{
          style: {
            borderRadius: '24px',
            background: 'rgba(255, 255, 255, 0.1)',
            backdropFilter: 'blur(20px)',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            boxShadow: '0 4px 20px rgba(0,0,0,0.08)'
          },
          sx: {
            '& .MuiDialogContent-root': {
              backgroundColor: 'transparent !important',
              background: 'transparent !important'
            },
            '& .MuiDialogTitle-root': {
              backgroundColor: 'transparent !important',
              background: 'transparent !important'
            },
            '& .MuiDialogActions-root': {
              backgroundColor: 'transparent !important',
              background: 'transparent !important'
            },
            '& .MuiPaper-root': {
              backgroundColor: 'rgba(255, 255, 255, 0.1) !important',
              background: 'rgba(255, 255, 255, 0.1) !important'
            }
          }
        }}
      >
        <DialogTitle sx={{ pb: 1, backgroundColor: 'transparent' }} component="div">
          <Typography variant="h5" sx={{ fontWeight: 600, color: 'white' }}>
            {editingEvent ? 'Modifier l\'événement' : 'Nouvel événement'}
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ backgroundColor: 'transparent' }}>
          <Grid container spacing={3} sx={{ mt: 1 }}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Titre de l'événement"
                value={formData.titre}
                onChange={(e) => setFormData({...formData, titre: e.target.value})}
                sx={{ 
                  '& .MuiOutlinedInput-root': { 
                    borderRadius: 2,
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    color: 'white'
                  },
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'rgba(255, 255, 255, 0.2)'
                  },
                  '& .MuiInputLabel-root': {
                    color: 'rgba(255, 255, 255, 0.7)'
                  },
                  '& .MuiInputBase-input::placeholder': {
                    color: 'rgba(255, 255, 255, 0.5)',
                    opacity: 1
                  }
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel sx={{ 
                  color: 'rgba(255, 255, 255, 0.7)',
                  '&.Mui-focused': {
                    color: 'rgba(255, 255, 255, 0.9)'
                  }
                }}>Type d'événement</InputLabel>
                <Select
                  value={formData.type}
                  label="Type d'événement"
                  onChange={(e) => setFormData({...formData, type: e.target.value})}
                  MenuProps={{
                    PaperProps: {
                      sx: {
                        background: 'rgba(255, 255, 255, 0.1)',
                        backdropFilter: 'blur(20px)',
                        border: '1px solid rgba(255, 255, 255, 0.2)',
                        borderRadius: 2,
                        mt: 1,
                        '& .MuiMenuItem-root': {
                          color: 'white',
                          '&:hover': {
                            backgroundColor: 'rgba(255, 255, 255, 0.1)'
                          },
                          '&.Mui-selected': {
                            backgroundColor: 'rgba(255, 255, 255, 0.2)',
                            '&:hover': {
                              backgroundColor: 'rgba(255, 255, 255, 0.3)'
                            }
                          }
                        }
                      }
                    }
                  }}
                  sx={{ 
                    borderRadius: 2,
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    color: 'white',
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'rgba(255, 255, 255, 0.2)'
                    },
                    '& .MuiSelect-icon': {
                      color: 'rgba(255, 255, 255, 0.7)'
                    }
                  }}
                >
                  <MenuItem value="Départ">Départ</MenuItem>
                  <MenuItem value="Retour">Retour</MenuItem>
                  <MenuItem value="Professionnel">Professionnel</MenuItem>
                  <MenuItem value="Formation">Formation</MenuItem>
                  <MenuItem value="Événement">Événement</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Date"
                type="date"
                value={formData.date}
                onChange={(e) => setFormData({...formData, date: e.target.value})}
                InputLabelProps={{ shrink: true }}
                sx={{ 
                  '& .MuiOutlinedInput-root': { 
                    borderRadius: 2,
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    color: 'white'
                  },
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'rgba(255, 255, 255, 0.2)'
                  },
                  '& .MuiInputLabel-root': {
                    color: 'rgba(255, 255, 255, 0.7)'
                  }
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Heure"
                type="time"
                value={formData.heure}
                onChange={(e) => setFormData({...formData, heure: e.target.value})}
                InputLabelProps={{ shrink: true }}
                sx={{ 
                  '& .MuiOutlinedInput-root': { 
                    borderRadius: 2,
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    color: 'white'
                  },
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'rgba(255, 255, 255, 0.2)'
                  },
                  '& .MuiInputLabel-root': {
                    color: 'rgba(255, 255, 255, 0.7)'
                  }
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Autocomplete
                value={selectedClient}
                onChange={(event, newValue) => {
                  setSelectedClient(newValue);
                  setFormData({...formData, client: newValue});
                }}
                options={clients || []}
                getOptionLabel={(option) => `${option.prenom || ''} ${option.nom || ''}`}
                renderOption={(props, option) => {
                  const { key, ...otherProps } = props;
                  return (
                    <Box component="li" key={option.id} {...otherProps}>
                      <Avatar sx={{ width: 32, height: 32, mr: 2 }}>
                        {option.prenom?.charAt(0)}{option.nom?.charAt(0)}
                      </Avatar>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="subtitle2">{option.prenom} {option.nom}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {option.niveauFidelite || 'Standard'} • {option.email}
                        </Typography>
                      </Box>
                    </Box>
                  );
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Client"
                    placeholder="Sélectionner un client"
                    sx={{ 
                      '& .MuiOutlinedInput-root': { 
                        borderRadius: 2,
                        backgroundColor: 'rgba(255, 255, 255, 0.1)',
                        color: 'white'
                      },
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'rgba(255, 255, 255, 0.2)'
                      },
                      '& .MuiInputLabel-root': {
                        color: 'rgba(255, 255, 255, 0.7)'
                      },
                      '& .MuiInputBase-input::placeholder': {
                        color: 'rgba(255, 255, 255, 0.5)',
                        opacity: 1
                      }
                    }}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Autocomplete
                value={selectedPackage}
                onChange={(event, newValue) => {
                  setSelectedPackage(newValue);
                  setFormData({...formData, package: newValue});
                  
                  // Auto-update destination and revenue if package is selected
                  if (newValue) {
                    setFormData(prev => ({
                      ...prev,
                      destination: newValue.destination,
                      revenue: newValue.prix * prev.participants
                    }));
                  }
                }}
                options={packages || []}
                getOptionLabel={(option) => option.titre || ''}
                renderOption={(props, option) => {
                  const { key, ...otherProps } = props;
                  return (
                    <Box component="li" key={option.id} {...otherProps}>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="subtitle2">{option.titre}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {option.destination} • {formatCurrency(option.prix)} • {option.duree}
                        </Typography>
                      </Box>
                    </Box>
                  );
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Forfait"
                    placeholder="Sélectionner un forfait"
                    sx={{ 
                      '& .MuiOutlinedInput-root': { 
                        borderRadius: 2,
                        backgroundColor: 'rgba(255, 255, 255, 0.1)',
                        color: 'white'
                      },
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'rgba(255, 255, 255, 0.2)'
                      },
                      '& .MuiInputLabel-root': {
                        color: 'rgba(255, 255, 255, 0.7)'
                      },
                      '& .MuiInputBase-input::placeholder': {
                        color: 'rgba(255, 255, 255, 0.5)',
                        opacity: 1
                      }
                    }}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Lieu/Destination"
                value={formData.destination}
                onChange={(e) => setFormData({...formData, destination: e.target.value})}
                sx={{ 
                  '& .MuiOutlinedInput-root': { 
                    borderRadius: 2,
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    color: 'white'
                  },
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'rgba(255, 255, 255, 0.2)'
                  },
                  '& .MuiInputLabel-root': {
                    color: 'rgba(255, 255, 255, 0.7)'
                  },
                  '& .MuiInputBase-input::placeholder': {
                    color: 'rgba(255, 255, 255, 0.5)',
                    opacity: 1
                  }
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel sx={{ 
                  color: 'rgba(255, 255, 255, 0.7)',
                  '&.Mui-focused': {
                    color: 'rgba(255, 255, 255, 0.9)'
                  }
                }}>Statut</InputLabel>
                <Select
                  value={formData.statut}
                  label="Statut"
                  onChange={(e) => setFormData({...formData, statut: e.target.value})}
                  sx={{ 
                    borderRadius: 2,
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    color: 'white',
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'rgba(255, 255, 255, 0.2)'
                    },
                    '& .MuiSelect-icon': {
                      color: 'rgba(255, 255, 255, 0.7)'
                    }
                  }}
                >
                  <MenuItem value="Planifié">Planifié</MenuItem>
                  <MenuItem value="Confirmé">Confirmé</MenuItem>
                  <MenuItem value="En attente">En attente</MenuItem>
                  <MenuItem value="Annulé">Annulé</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Nombre de participants"
                type="number"
                value={formData.participants}
                onChange={(e) => {
                  const newParticipants = parseInt(e.target.value);
                  setFormData({
                    ...formData,
                    participants: newParticipants,
                    // Update revenue if package is selected
                    revenue: selectedPackage ? selectedPackage.prix * newParticipants : formData.revenue
                  });
                }}
                sx={{ 
                  '& .MuiOutlinedInput-root': { 
                    borderRadius: 2,
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    color: 'white'
                  },
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'rgba(255, 255, 255, 0.2)'
                  },
                  '& .MuiInputLabel-root': {
                    color: 'rgba(255, 255, 255, 0.7)'
                  }
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Revenu estimé"
                type="number"
                value={formData.revenue}
                onChange={(e) => setFormData({...formData, revenue: parseInt(e.target.value)})}
                disabled={!!selectedPackage}
                sx={{ 
                  '& .MuiOutlinedInput-root': { 
                    borderRadius: 2,
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    color: 'white'
                  },
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'rgba(255, 255, 255, 0.2)'
                  },
                  '& .MuiInputLabel-root': {
                    color: 'rgba(255, 255, 255, 0.7)'
                  }
                }}
                InputProps={{
                  startAdornment: (
                    <Typography variant="body2" sx={{ mr: 1, color: 'rgba(255, 255, 255, 0.7)' }}>
                      {settings.defaultCurrency}
                    </Typography>
                  )
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                multiline
                rows={3}
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                placeholder="Détails de l'événement..."
                sx={{ 
                  '& .MuiOutlinedInput-root': { 
                    borderRadius: 2,
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    color: 'white'
                  },
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'rgba(255, 255, 255, 0.2)'
                  },
                  '& .MuiInputLabel-root': {
                    color: 'rgba(255, 255, 255, 0.7)'
                  },
                  '& .MuiInputBase-input::placeholder': {
                    color: 'rgba(255, 255, 255, 0.5)',
                    opacity: 1
                  }
                }}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 1, backgroundColor: 'transparent' }}>
          <Button onClick={handleCloseDialog} sx={{ borderRadius: 2, color: 'white' }}>
            Annuler
          </Button>
          {editingEvent && (
            <Button 
              onClick={handleDeleteEvent}
              variant="outlined"
              color="error"
              startIcon={<DeleteIcon />}
              sx={{ 
                borderRadius: 2, 
                mr: 'auto',
                borderColor: 'rgba(255, 100, 100, 0.5)',
                color: 'rgba(255, 100, 100, 0.8)',
                '&:hover': {
                  borderColor: 'rgba(255, 100, 100, 0.8)',
                  backgroundColor: 'rgba(255, 100, 100, 0.1)'
                }
              }}
            >
              Supprimer
            </Button>
          )}
          <Button 
            onClick={handleSaveEvent}
            variant="contained"
            sx={{ 
              borderRadius: 2,
              background: 'linear-gradient(135deg, #1e40af 0%, #7c3aed 100%)',
              color: 'white',
              '&:hover': {
                background: 'linear-gradient(135deg, #1e3a8a 0%, #6d28d9 100%)'
              }
            }}
          >
            {editingEvent ? 'Mettre à jour' : 'Créer'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({...snackbar, open: false})}
      >
        <Alert 
          onClose={() => setSnackbar({...snackbar, open: false})} 
          severity={snackbar.severity}
          sx={{ borderRadius: 2 }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Calendrier; 